import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseSource } from '~/lib/constants/survey/SurveyResponseSource';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import urlConstants from '~/lib/constants/url.constants';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import {
  GetSafetyRiskAlertsResponse,
  normalizeGetAlert,
  SafetyRiskAlert,
  SafetyRiskAlertStatus,
} from '~/lib/types/survey/SafetyRiskAlert';
import { joinQueryParams } from '~/lib/util/api';
import api from '~/store/services/api';
import { CacheTag } from '~/store/services/CacheTag';

import { HttpRequestMethod } from '..';

// Fixed page size the infinite grouped-assessments query pages through. Kept
// internal (not a request arg) so the single cache entry can't fragment on it.
const GROUPED_ASSESSMENTS_V2_PAGE_SIZE = 30;

type GetSurveysSortByOption = 'surveyName';
export type GetSurveysResponse = { count: number; surveys: Survey[] };
export type GetAssessmentsListRequest = {
  pageSize?: number;
  patientId: string;
  filterBy?: { surveyName?: string };
};
export type GetGroupedAssessmentsResponse = { surveys: GroupedAssessment[] };
export type GetGroupedAssessmentsRequest = { patientId: string };
export type GetGroupedAssessmentsV2Response = {
  surveys: GroupedAssessment[];
  hasMore: boolean;
};
export type GetGroupedAssessmentsV2InfiniteRequest = {
  patientId: string;
  sinceDate: string;
};
export type GetSurveysRequest = {
  pageSize?: number;
  pageNumber?: number;
  sortBy?: GetSurveysSortByOption;
  filterBy?: { surveyName?: string };
};

type CreateAssessmentRequest = {
  assigneeExternalId: string;
  status: SurveyResponseStatus;
  submittedSource: SurveyResponseSource;
  submittedUserFirstName: string;
  submittedUserLastName: string;
  submittedUserId: string;
  surveyName: SurveyName;
  toUserEmail: string;
  toUserFirstName: string;
  toUserLastName: string;
  toUserPhone: string;
  userId: string;
};

type UpdateAssessmentRequest = {
  responseExternalId: string;
  assessment: {
    status: SurveyResponseStatus;
    submittedSource: SurveyResponseSource;
    submittedUserFirstName: string;
    submittedUserLastName: string;
    submittedUserId: string;
    toUserEmail: string;
    userTimezoneId: string;
    jsonResults?: string;
  };
};

const { assessments } = urlConstants;

export const surveysApi = api.injectEndpoints({
  endpoints: (build) => ({
    createAssessment: build.mutation<SurveyResponse, CreateAssessmentRequest>({
      invalidatesTags: [CacheTag.AssessmentsList, CacheTag.GroupedAssessments],
      query: (req) => ({
        body: req,
        method: HttpRequestMethod.POST,
        url: assessments.createAssessment,
      }),
    }),
    deleteAssessment: build.mutation<void, string>({
      invalidatesTags: [CacheTag.AssessmentsList, CacheTag.GroupedAssessments],
      query: (responseExternalId) => ({
        method: HttpRequestMethod.DELETE,
        url: assessments.deleteAssessment(responseExternalId),
      }),
    }),
    getAssessmentById: build.query<SurveyResponse, string>({
      keepUnusedDataFor: 10 * 60,
      providesTags: (_result, _error, responseExternalId) => [
        { id: responseExternalId, type: CacheTag.Assessment },
      ],
      query: (responseExternalId: string) => ({
        url: assessments.getAssessmentById(responseExternalId),
      }),
    }),
    getAssessmentsList: build.query<
      AssessmentListResponse,
      GetAssessmentsListRequest
    >({
      keepUnusedDataFor: 3 * 60,
      providesTags: [CacheTag.AssessmentsList],
      query: ({ pageSize = 150, patientId, filterBy }) => {
        return {
          params: {
            'filter-by': joinQueryParams({ userId: patientId, ...filterBy }),
            'page-size': pageSize, // pagination not supported (on the FE?)
            'sort-by': 'updatedDate', // only sort-by currently in use by FE
          },
          url: assessments.listAssessments,
        };
      },
    }),
    getAssessmentSummariesByInquiryId: build.query<
      AssessmentSummariesResponse,
      string
    >({
      keepUnusedDataFor: 0,
      query: (inquiryId: string) => ({
        url: assessments.getAssessmentSummariesByInquiryId(inquiryId),
      }),
    }),
    getGroupedAssessments: build.query<
      GetGroupedAssessmentsResponse,
      GetGroupedAssessmentsRequest
    >({
      keepUnusedDataFor: 6 * 60,
      providesTags: [CacheTag.GroupedAssessments],
      query: ({ patientId }) => ({
        url: assessments.groupedAssessments(patientId),
      }),
    }),
    // Infinite variant of the grouped-assessments query. All pages accumulate
    // under a single cache entry keyed by {patientId, sinceDate}, so a mutation that
    // invalidates GroupedAssessments re-drives the entire history rather than a
    // single pinned page. This is the fix for stale create/delete/submit state
    // on the assessments tab (GX-3570). The page size is a fixed internal detail
    // (not a request arg), so the cache entry can never fragment on it.
    getGroupedAssessmentsV2Infinite: build.infiniteQuery<
      GetGroupedAssessmentsV2Response,
      GetGroupedAssessmentsV2InfiniteRequest,
      number
    >({
      keepUnusedDataFor: 6 * 60,
      providesTags: [CacheTag.GroupedAssessments],
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) =>
          lastPage.hasMore ? allPages.length + 1 : undefined,
      },
      query: ({ queryArg: { patientId, sinceDate }, pageParam }) => ({
        url: assessments.groupedAssessmentsV2(patientId),
        params: {
          'since-date': sinceDate,
          'page-size': GROUPED_ASSESSMENTS_V2_PAGE_SIZE,
          'page-number': pageParam,
        },
      }),
    }),
    getSurveys: build.query<GetSurveysResponse, GetSurveysRequest>({
      keepUnusedDataFor: 5 * 60,
      query: ({ pageSize, pageNumber, sortBy, filterBy }) => {
        const params = {};
        if (pageSize !== undefined) {
          params['page-size'] = pageSize;
        }
        if (pageNumber !== undefined) {
          params['page-number'] = pageNumber;
        }
        if (sortBy !== undefined) {
          params['sort-by'] = sortBy;
        }
        if (filterBy !== undefined) {
          params['filter-by'] = joinQueryParams(filterBy);
        }
        return {
          params,
          url: assessments.listSurveys,
        };
      },
    }),
    updateAssessment: build.mutation<string, UpdateAssessmentRequest>({
      invalidatesTags: (_result, _error, { responseExternalId }) => [
        CacheTag.AssessmentsList,
        CacheTag.GroupedAssessments,
        { id: responseExternalId, type: CacheTag.Assessment },
      ],
      query: ({ responseExternalId, assessment }) => ({
        body: assessment,
        method: HttpRequestMethod.PATCH,
        responseHandler: 'text',
        url: assessments.updateAssessment(responseExternalId),
      }),
    }),
    getSafetyRiskAlerts: build.query<
      SafetyRiskAlert[],
      { providerId: string; statuses: SafetyRiskAlertStatus[] }
    >({
      keepUnusedDataFor: 0,
      providesTags: [CacheTag.SafetyRiskAlerts],
      query: ({ providerId, statuses }) => ({
        params: {
          providerId,
          status: statuses.join(','),
        },
        url: assessments.getSafetyRiskAlerts,
      }),
      transformResponse: (response: GetSafetyRiskAlertsResponse) =>
        response.alerts.map(normalizeGetAlert),
    }),
    updateSafetyRiskAlert: build.mutation<
      void,
      { safetyRiskAlertId: string; status: SafetyRiskAlertStatus }
    >({
      invalidatesTags: [CacheTag.SafetyRiskAlerts],
      query: ({ safetyRiskAlertId, status }) => ({
        body: { status },
        method: HttpRequestMethod.PATCH,
        responseHandler: 'text',
        url: assessments.updateSafetyRiskAlert(safetyRiskAlertId),
      }),
    }),
  }),
});

export const {
  useCreateAssessmentMutation,
  useDeleteAssessmentMutation,
  useGetAssessmentByIdQuery,
  useGetAssessmentsListQuery,
  useGetAssessmentSummariesByInquiryIdQuery,
  useGetGroupedAssessmentsQuery,
  useGetSafetyRiskAlertsQuery,
  useGetSurveysQuery,
  useUpdateAssessmentMutation,
  useUpdateSafetyRiskAlertMutation,
} = surveysApi;

// The top-level `use…InfiniteQuery` shorthand is not surfaced on the injected
// api's inferred type in this RTK version, so reference the hook via the
// endpoint (which is correctly typed) instead.
export const useGetGroupedAssessmentsV2InfiniteQuery =
  surveysApi.endpoints.getGroupedAssessmentsV2Infinite.useInfiniteQuery;
